import siteConfig from "site-config";
import fetch, { fetchJSON } from "lib/fetch";
import {
    useQuery,
    useMutation,
    queryCache,
    QueryConfig,
    useInfiniteQuery,
    InfiniteQueryConfig,
    MutateConfig,
    QueryKey,
} from "react-query";
import { IAsset } from "constants/assets";
import { MEDIA_TYPE_IMAGE, MEDIA_TYPE_360 } from "constants/media";
import lodashIsEmpty from "lodash/isEmpty";
import { stringify } from "query-string";
import { FilterKind } from "./";

export const SITES = "sites";

export enum SiteKind {
    Tradeshow = "TRADESHOW",
    FashionWeek = "FASHION_WEEK",
    Brand = "BRAND",
}

export enum AssetField {
    HeroImage = "hero_image",
    LogoImage = "logo_image",
    TileImage = "tile_image",
    TileImagePlaceholder = "tile_image_placeholder",
}

export enum FilterValueField {
    String = "string_value",
    Boolean = "bool_value",
    Timestamp = "timestamp_value",
}

export enum DefaultOrder {
    Random = "RANDOM",
    Alphabetical = "ALPHABETICAL",
    ReverseAlphabetical = "REVERSE_ALPHABETICAL",
}

export const vanityField = "slug";
export interface ISite {
    active: boolean;
    id: string;
    slug: string;
    name: string;
    home_page_id: string;
    kind: SiteKind;
    created_at: string;
    modified_at: string;
    event_from: string;
    event_to: string;
    merchant_id: string;
    hero_image: IAsset;
    logo_image: IAsset;
    tile_image: IAsset;
    tile_image_placeholder: IAsset;
    description: string;
    global_search: boolean;
    default_order: DefaultOrder;
    buyer_registration_url: string;
    brand_id: string;
    copyable: boolean;
}

export interface ISitesResponse {
    data: ISite[];
}

export interface IListSitesRequest {
    merchant_id?: string;
    brand_id?: string;
    limit?: number;
    min_id?: string;
    active?: boolean;
    filters?: ISiteFilter;
}

export enum SiteFilterableField {
    MerchantId = "MERCHANT_ID",
    PageBrandId = "PAGE_BRAND_ID",
    EventFrom = "EVENT_FROM",
    EventTo = "EVENT_TO",
    Active = "ACTIVE",
    BrandId = "SITE_BRAND_ID",
    Kind = "SITE_KIND",
    Copyable = "COPYABLE",
}

interface FilterValue {
    [FilterValueField.String]?: string;
    [FilterValueField.Boolean]?: boolean;
    [FilterValueField.Timestamp]?: Date;
}

interface IEqualFilter {
    [FilterKind.Eq]: {
        field: SiteFilterableField;
        value: FilterValue;
    };
}

interface IAndFilter {
    [FilterKind.And]: {
        filters: ISiteFilter;
    };
}

interface IOrFilter {
    [FilterKind.Or]: {
        filters: ISiteFilter;
    };
}

export type ISiteFilter = Array<IAndFilter | IOrFilter | IEqualFilter>;

function buildCacheKeys(filter?: Partial<IListSitesRequest>): QueryKey {
    const compositeIds = { filter: undefined };

    if (filter && Object.keys(filter).length) {
        compositeIds.filter = filter;
    }

    return [SITES, compositeIds];
}

export const useFetchSites = (query?: IListSitesRequest) => {
    return useQuery<ISitesResponse, string>(SITES, () =>
        fetchJSON<ISitesResponse>(
            `${siteConfig.goAPI}/v1/sites${!lodashIsEmpty(query) ? `?${stringify(query)}` : ""}`,
            {
                method: "GET",
                noDomain: true,
            }
        )
    );
};

export const useInfiniteFetchSites = (
    query: IListSitesRequest,
    options?: Partial<InfiniteQueryConfig<ISitesResponse, string>>
) => {
    return useInfiniteQuery<ISitesResponse, QueryKey>(
        buildCacheKeys(query),
        (_sitesKey, _filterKey, minId: string) => {
            query.min_id = minId;
            return fetchJSON<ISitesResponse>(`${siteConfig.goAPI}/v1/sites/with_filter`, {
                method: "POST",
                body: JSON.stringify(query),
                noDomain: true,
            });
        },
        {
            ...options,
            getFetchMore: (lastpage) => {
                const minId = lastpage.data[lastpage.data.length - 1]?.id;
                return lastpage.data.length === query?.limit ? minId : false;
            },
        }
    );
};

export const useFetchSiteById = (siteId: string, query?: IListSitesRequest, options?: QueryConfig<ISite>) => {
    return useQuery<ISite, [string, { site: string }]>(
        [SITES, { site: siteId }],
        () =>
            fetchJSON<ISite>(
                `${siteConfig.goAPI}/v1/sites/${siteId}${!lodashIsEmpty(query) ? `?${stringify(query)}` : ""}`,
                {
                    method: "GET",
                    noDomain: true,
                }
            ),
        options
    );
};

export const useFetchSiteBySlugs = (slugs: string[], options?: QueryConfig<ISite>) => {
    const HTTPOptions = {
        method: "GET",
        noDomain: true,
    };

    const buildURL = ({ slug }) => `${siteConfig.goAPI}/v1/sites/slug/${slug.toLowerCase()}`;

    return slugs.map((slug) =>
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useQuery<ISite, [string, { slug: string }]>(
            [SITES, { slug }],
            () => fetchJSON<ISite>(buildURL({ slug }), HTTPOptions),
            options
        )
    );
};

export const useFetchSiteBySlug = (slug: string, options?: QueryConfig<ISite>) => {
    return useQuery<ISite, [string, { slug: string }]>(
        [SITES, { slug }],
        () =>
            fetchJSON<ISite>(`${siteConfig.goAPI}/v1/sites/slug/${slug.toLowerCase()}`, {
                method: "GET",
                noDomain: true,
            }),
        options
    );
};

export const useCreateSite = (mutationObject?: MutateConfig<ISite, Error[]>) => {
    return useMutation(createSite, {
        ...mutationObject,
        onSuccess: (data: ISite) => {
            queryCache.invalidateQueries(SITES);
            mutationObject?.onSuccess?.(data, {});
        },
        onError: (error, variables, snap) => {
            queryCache.invalidateQueries(SITES);
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export const usePatchSite = (mutationObject?: MutateConfig<ISite, Error[]>) => {
    return useMutation(patchSite, {
        ...mutationObject,
        onSuccess: (data: ISite) => {
            queryCache.invalidateQueries([SITES, { site: data.id }]);
            mutationObject?.onSuccess?.(data, {});
        },
        onError: (error, variables: IPatchSiteRequest, snap) => {
            queryCache.invalidateQueries([SITES, { site: variables?.site?.id }]);
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export const useCreateAsset = (onSuccess?: (data: IAsset) => void) => {
    return useMutation(createAsset, {
        onSuccess: (data: IAsset) => {
            onSuccess?.(data);
        },
    });
};

export const useCreateAssets = (onSuccess?: (data: IAsset) => void) => {
    return useMutation(createAssets, {
        onSuccess: (data: IAsset) => {
            onSuccess?.(data);
        },
    });
};

function createSite(site: Partial<ISite>) {
    return fetch(`${siteConfig.goAPI}/v1/sites`, {
        method: "POST",
        noDomain: true,
        body: JSON.stringify(site),
    }).then((response) => {
        return response.json();
    });
}

export interface IPatchSiteRequest {
    site: Partial<ISite>;
    field_mask: { paths: string[] };
}

function patchSite(data: IPatchSiteRequest) {
    return fetchJSON<ISite>(`${siteConfig.goAPI}/v1/sites/${data.site.id}`, {
        method: "PATCH",
        noDomain: true,
        body: JSON.stringify(data),
    });
}

function createAsset({ siteId, uploadId, brandId }: { siteId: string; uploadId: string; brandId?: string }) {
    const payload = {
        kind: MEDIA_TYPE_IMAGE,
        media_resource: { image: { upload_id: uploadId } },
        // It's likely `brandId` is empty string. Defaults to `undefined` so key won't be sent
        brand_id: brandId || undefined,
    };

    return fetchJSON<IAsset>(`${siteConfig.goAPI}/v1/sites/${siteId}/assets`, {
        method: "POST",
        noDomain: true,
        body: JSON.stringify(payload),
    });
}

function createAssets({ siteId, uploadIds, brandId }: { siteId: string; uploadIds: string[]; brandId?: string }) {
    const payload = {
        kind: MEDIA_TYPE_360,
        media_resource: { image_360: { upload_ids: { values: uploadIds } } },
        brand_id: brandId || undefined,
    };

    return fetchJSON<IAsset>(`${siteConfig.goAPI}/v1/sites/${siteId}/assets`, {
        method: "POST",
        noDomain: true,
        body: JSON.stringify(payload),
    });
}

export const useDeleteSite = (filter: Partial<IListSitesRequest> = {}) => {
    return useMutation(deleteSite, {
        onSuccess: (_data) => {
            queryCache.invalidateQueries(buildCacheKeys(filter));
        },
        onError: (error) => {
            // left blank for now
        },
    });
};

function deleteSite(siteId: string) {
    return fetchJSON<{}>(`${siteConfig.goAPI}/v1/sites/${siteId}`, {
        method: "DELETE",
        noDomain: true,
    });
}
